<template>
  <div
    class="relative inline-flex items-center justify-center rounded-full overflow-hidden"
    :class="sizeClasses"
    :style="avatarStyle"
  >
    <img
      v-if="showImage"
      :src="src || ''"
      :alt="name"
      class="w-full h-full object-cover object-center"
      @error="handleImageError"
    />
    <span v-else class="font-semibold" :class="textSizeClasses">
      {{ initials }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { logDebug, logError } from "@/utils/secureLogger";

export interface UserAvatarProps {
  src?: string | null;
  name: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
}

const props = withDefaults(defineProps<UserAvatarProps>(), {
  src: null,
  size: "2xl",
});

const imageLoadError = ref(false);

// Watch for src changes and reset error state
watch(
  () => props.src,
  (newSrc) => {
    imageLoadError.value = false;
    logDebug("UserAvatar source updated", {
      name: props.name,
      hasSource: Boolean(newSrc),
    });
  }
);

// Debug: Log when image fails to load
const handleImageError = (event: Event) => {
  imageLoadError.value = true;
  const errorDetails = event instanceof Error ? event : new Error("Image failed to load");
  logError("UserAvatar failed to load image", errorDetails);
  logDebug("UserAvatar image load failure details", {
    name: props.name,
    hasSource: Boolean(props.src),
    eventType: event.type,
  });
};

// Generate initials from name
const initials = computed(() => {
  if (!props.name) return "?";

  const nameParts = props.name.trim().split(" ").filter(Boolean);

  if (nameParts.length === 0) return "?";
  if (nameParts.length === 1) return nameParts[0].charAt(0).toUpperCase();

  // First letter of first name + first letter of last name
  return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
});

// Deterministic color generation based on name - Tailwind palette
const backgroundColor = computed(() => {
  const colors = [
    "#3B82F6", // Blue-500
    "#10B981", // Green-500
    "#F59E0B", // Yellow-500 (Amber)
    "#8B5CF6", // Purple-500
    "#06B6D4", // Cyan-500
    "#EC4899", // Pink-500
    "#6366F1", // Indigo-500
    "#14B8A6", // Teal-500
    "#60A5FA", // Blue-400 (lighter)
    "#34D399", // Green-400 (lighter)
    "#FBBF24", // Yellow-400 (lighter)
    "#A78BFA", // Purple-400 (lighter)
  ];

  // Generate consistent index based on name
  let hash = 0;
  for (let i = 0; i < props.name.length; i++) {
    hash = props.name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const index = Math.abs(hash) % colors.length;
  return colors[index];
});

// Determine if we should show image or initials
const showImage = computed(() => {
  return props.src && !imageLoadError.value;
});

const avatarStyle = computed(() => {
  if (showImage.value) {
    return {};
  }

  return {
    backgroundColor: backgroundColor.value,
    color: "#FFFFFF", // White text for Tailwind vibrant colors
  };
});

const sizeClasses = computed(() => {
  const sizes = {
    xs: "w-6 h-6 text-xs",
    sm: "w-8 h-8 text-sm",
    md: "w-10 h-10 text-base",
    lg: "w-12 h-12 text-lg",
    xl: "w-16 h-16 text-2xl",
    "2xl": "w-20 h-20 text-3xl",
  };

  return sizes[props.size];
});

const textSizeClasses = computed(() => {
  const sizes = {
    xs: "text-[10px]",
    sm: "text-xs",
    md: "text-sm",
    lg: "text-base",
    xl: "text-xl",
    "2xl": "text-2xl",
  };

  return sizes[props.size];
});
</script>

<style scoped>
/* Smooth transition for image loading */
img {
  transition: opacity 0.2s ease-in-out;
  /* Ensure image fills the entire circular container */
  min-width: 100%;
  min-height: 100%;
  /* Center the image within the circle */
  object-position: center center;
}
</style>
